import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useLocation as useLocationCtx } from '../context/LocationContext';
import apiService from '../services/apiService';
import { resolveImageUrl } from '../services/apiService';
import { LoadingSpinner, StarRating, ConfirmModal, formatUSPhone } from '../components';
import { Heart, Search, MapPin, Phone, Leaf, Calendar, Tag, ShoppingCart, Trash2, ArrowLeft, CheckCircle, Info, X, ChevronLeft, ChevronRight } from 'lucide-react';

// US state abbreviation → full name
const US_STATES = {
  AL:'Alabama', AK:'Alaska', AZ:'Arizona', AR:'Arkansas', CA:'California',
  CO:'Colorado', CT:'Connecticut', DE:'Delaware', FL:'Florida', GA:'Georgia',
  HI:'Hawaii', ID:'Idaho', IL:'Illinois', IN:'Indiana', IA:'Iowa',
  KS:'Kansas', KY:'Kentucky', LA:'Louisiana', ME:'Maine', MD:'Maryland',
  MA:'Massachusetts', MI:'Michigan', MN:'Minnesota', MS:'Mississippi', MO:'Missouri',
  MT:'Montana', NE:'Nebraska', NV:'Nevada', NH:'New Hampshire', NJ:'New Jersey',
  NM:'New Mexico', NY:'New York', NC:'North Carolina', ND:'North Dakota', OH:'Ohio',
  OK:'Oklahoma', OR:'Oregon', PA:'Pennsylvania', RI:'Rhode Island', SC:'South Carolina',
  SD:'South Dakota', TN:'Tennessee', TX:'Texas', UT:'Utah', VT:'Vermont',
  VA:'Virginia', WA:'Washington', WV:'West Virginia', WI:'Wisconsin', WY:'Wyoming',
  DC:'Washington DC',
};
const expandState = (s) => { if (!s) return ''; const u = s.trim().toUpperCase(); return US_STATES[u] || s; };

// ── Cart helpers ──────────────────────────────────────────────────────────────
const PLATFORM_FEE = 5;
const TAX_RATE = 0.05;
const DEMO_COUPONS = { SAVE5: 5, BIRYANI10: 10, FIRST15: 15 };

function formatDateISO(date) {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function formatTimeDisplay(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const suffix = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${suffix}`;
}
function buildNextDays(count = 14) {
  return Array.from({ length: count }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() + i);
    return d;
  });
}
function shortDateLabel(d) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const date = new Date(d);
  date.setHours(0, 0, 0, 0);
  const diff = Math.round((date - today) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

// ── Main component ────────────────────────────────────────────────────────────
const KitchenDetailPage = () => {
  const { kitchenId, locationSlug, restaurantSlug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { selectedLocation, setSelectedLocation } = useLocationCtx();
  const {
    cartItems,
    cartRestaurant,
    addToCartWithRestaurant,
    replaceCartAndAdd,
    updateQuantity,
    removeFromCart,
    updateCookingRequest,
    getCartTotal,
    clearCart,
  } = useCart();

  const [kitchen, setKitchen] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [menuLoading, setMenuLoading] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);
  const [cartFeedback, setCartFeedback] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterVeg, setFilterVeg] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);
  const [showHoursModal, setShowHoursModal] = useState(false);
  const [showHoursTooltip, setShowHoursTooltip] = useState(false);

  // Replace-cart modal
  const [showReplaceModal, setShowReplaceModal] = useState(false);
  const [pendingItem, setPendingItem] = useState(null);

  // Cart panel state
  const [cookingInstructions, setCookingInstructions] = useState('');
  const [discount, setDiscount] = useState(0);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponInput, setCouponInput] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState('');
  const [showCouponInput, setShowCouponInput] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedTime, setSelectedTime] = useState(null);

  // ── Inline checkout state ─────────────────────────────────────────────────
  // panelMode: 'cart' | 'checkout' | 'confirmed'
  const [panelMode, setPanelMode] = useState('cart');
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [tipPct, setTipPct] = useState(0); // 0=None, -1=Custom
  const [customTipAmount, setCustomTipAmount] = useState('');
  const [placing, setPlacing] = useState(false);
  const [orderError, setOrderError] = useState('');
  const [confirmedOrderId, setConfirmedOrderId] = useState(null);

  // Category tabs scroll
  const catTabsRef = useRef(null);
  const [catScrollPos, setCatScrollPos] = useState(0);
  const [catMaxScroll, setCatMaxScroll] = useState(0);

  // Update max scroll when categories change
  useEffect(() => {
    const el = catTabsRef.current;
    if (!el) return;
    const update = () => setCatMaxScroll(el.scrollWidth - el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [menuItems]);

  const dates = buildNextDays(14);

  // ── Load kitchen + menu ───────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      setMenuLoading(true);
      try {
        let kid;
        
        // Check if restaurantSlug is actually a UUID (kitchen ID)
        const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(restaurantSlug);
        
        if (restaurantSlug && isUUID) {
          // restaurantSlug is actually a kitchen ID (location-based URL: /austin/uuid)
          kid = restaurantSlug;
        } else if (restaurantSlug && !isUUID) {
          // restaurantSlug is a name slug - fetch all kitchens and find by slug
          const kitchensRes = await apiService.getKitchens({ limit: 100 });
          if (kitchensRes?.success && kitchensRes?.data) {
            const allKitchens = kitchensRes.data.kitchens || kitchensRes.data || [];
            const kitchenData = allKitchens.find(k => {
              const name = k.kitchen_name || k.name || '';
              const slug = name.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').trim();
              return slug === restaurantSlug;
            });
            kid = kitchenData?.kitchen_id || kitchenData?.id;
          }
        } else if (kitchenId) {
          // Legacy ID-based URL (/kitchen/uuid)
          kid = kitchenId;
        }
        
        if (!kid) {
          console.error('Kitchen not found');
          return;
        }
        
        const [kitchenRes, menuRes] = await Promise.all([
          apiService.getKitchenById(kid),
          apiService.getKitchenMenu(kid),
        ]);
        if (kitchenRes?.success) {
          const k = kitchenRes.data?.kitchen || kitchenRes.data || {};
          setKitchen(k);
          setIsFavorite(k.is_favorite || false);
        }
        if (menuRes?.success) {
          const raw = menuRes.data || [];
          let flatItems = [];
          if (Array.isArray(raw)) {
            if (raw.length > 0 && raw[0]?.items) {
              raw.forEach(group => {
                (group.items || []).forEach(item => {
                  flatItems.push({ ...item, category: group.category || item.category || 'Menu' });
                });
              });
            } else {
              flatItems = raw;
            }
          } else if (raw.menu_items) {
            flatItems = raw.menu_items;
          } else if (raw.items) {
            flatItems = raw.items;
          }
          setMenuItems(flatItems);
        }
      } catch (e) {
        console.error('load kitchen error', e);
      } finally {
        setMenuLoading(false);
      }
    };
    if (kitchenId || restaurantSlug) load();
  }, [kitchenId, restaurantSlug]);

  // ── Sync locationSlug URL param → LocationContext ─────────────────────────
  // Ensures that opening /customer/dallas/nhh directly (or via refresh / back)
  // always sets "Dallas" as the selected location in context.
  useEffect(() => {
    if (!locationSlug) return;
    if (selectedLocation?.slug === locationSlug) return;
    const cityName = locationSlug
      .split('-')
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
    setSelectedLocation({ name: cityName, city: cityName, slug: locationSlug });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationSlug]);

  // ── Fetch pickup slots ────────────────────────────────────────────────────
  const fetchSlots = useCallback(async (kid, date) => {
    if (!kid) return;
    setSlotsLoading(true);
    setSelectedTime(null);
    const dateStr = formatDateISO(date);
    try {
      const res = await apiService.getKitchenPickupSlots(kid, dateStr);
      if (res?.success && res.data) {
        const now = new Date();
        const isToday = dateStr === formatDateISO(new Date());
        const available = (res.data.slots || []).filter(s => {
          if (!s.available) return false;
          if (isToday) {
            const [h, m] = (s.time || '').split(':').map(Number);
            if (isNaN(h)) return false;
            const st = new Date();
            st.setHours(h, m, 0, 0);
            return st > now;
          }
          return true;
        });
        setSlots(available);
        if (available.length > 0) setSelectedTime(available[0].time);
      } else {
        setSlots([]);
      }
    } catch {
      setSlots([]);
    } finally {
      setSlotsLoading(false);
    }
  }, []);

  useEffect(() => {
    // Always use the CART kitchen's ID for slots – the cart belongs to one kitchen.
    // Only fall back to the currently viewed kitchen if the cart is empty.
    const cartKid = cartItems.length > 0 ? cartRestaurant?.id : null;
    const kid = cartKid || kitchenId || kitchen?.kitchen_id || kitchen?.id;
    if (kid && selectedDate) fetchSlots(kid, selectedDate);
  }, [kitchenId, kitchen, selectedDate, fetchSlots, cartItems.length, cartRestaurant]);

  // ── Cart helpers ──────────────────────────────────────────────────────────
  const subtotal = getCartTotal();
  const tip = tipPct === -1
    ? (parseFloat(customTipAmount) || 0)
    : parseFloat(((tipPct / 100) * subtotal).toFixed(2));
  const tax = subtotal * TAX_RATE;
  const total = subtotal + PLATFORM_FEE + tax - discount;

  const getItemQuantityInCart = (itemId) => {
    const item = cartItems.find(i => i.id === itemId);
    return item ? item.quantity : 0;
  };

  const handleAddToCart = (item) => {
    if (!isAuthenticated) { navigate('/customer/login'); return; }
    const kid = kitchenId || kitchen?.kitchen_id || kitchen?.id;
    const restaurant = {
      id: kid,
      name: kitchen?.kitchen_name || kitchen?.name || '',
      address: [kitchen?.address_street, kitchen?.address_city, expandState(kitchen?.address_state), kitchen?.address_zip].filter(Boolean).join(', '),
      profile_image: kitchen?.profile_image_url || kitchen?.profile_image || kitchen?.logo_url || null,
    };
    const cartItem = {
      id: item.item_id || item.id,
      name: item.item_name || item.name,
      price: parseFloat(item.price || 0),
      image_url: item.image_url || null,
      is_veg: item.is_veg !== undefined ? item.is_veg : item.food_type === 'veg',
      description: item.description || '',
      kitchenId: kid,
    };
    const result = addToCartWithRestaurant(cartItem, restaurant);
    if (result?.conflict) {
      setPendingItem({ cartItem, restaurant });
      setShowReplaceModal(true);
    } else {
      showCartFeedback(cartItem.name + ' added!');
    }
  };

  const handleReplaceCart = () => {
    if (pendingItem) {
      replaceCartAndAdd(pendingItem.cartItem, pendingItem.restaurant);
      showCartFeedback(pendingItem.cartItem.name + ' added!');
    }
    setShowReplaceModal(false);
    setPendingItem(null);
  };

  const showCartFeedback = (msg) => {
    setCartFeedback(msg);
    setTimeout(() => setCartFeedback(''), 2000);
  };

  const handleToggleFavorite = async () => {
    if (!isAuthenticated) { navigate('/customer/login'); return; }
    const kid = kitchenId || kitchen?.kitchen_id || kitchen?.id;
    const newFav = !isFavorite;
    setIsFavorite(newFav);
    if (newFav) await apiService.addToFavorites(kid);
    else await apiService.removeFromFavorites(kid);
  };

  const handleApplyCoupon = () => {
    const code = couponInput.trim().toUpperCase();
    if (!code) { setCouponError('Please enter a coupon code'); return; }
    setCouponLoading(true);
    setCouponError('');
    setTimeout(() => {
      if (DEMO_COUPONS[code]) {
        setDiscount(DEMO_COUPONS[code]);
        setAppliedCoupon({ code, amount: DEMO_COUPONS[code] });
        setCouponInput('');
        setShowCouponInput(false);
      } else {
        setCouponError('Try SAVE5, BIRYANI10, or FIRST15');
      }
      setCouponLoading(false);
    }, 400);
  };

  const handleCheckout = () => {
    if (!isAuthenticated) { navigate('/customer/login'); return; }
    if (cartItems.length === 0) { alert('Your cart is empty'); return; }
    if (!selectedDate || !selectedTime) { alert('Please select a pickup date and time'); return; }
    setPaymentMethod(null);
    setOrderError('');
    setPanelMode('checkout');
  };

  const buildOrderData = (pmLabel) => {
    // Order must always go to the CART kitchen, not the currently viewed one
    const kid = cartRestaurant?.id || kitchenId || kitchen?.kitchen_id || kitchen?.id;
    const [ph, pm] = (selectedTime || '12:00').split(':').map(Number);
    const pickupStart = new Date(selectedDate);
    pickupStart.setHours(ph, pm, 0, 0);
    const pickupEnd = new Date(pickupStart);
    pickupEnd.setMinutes(pickupEnd.getMinutes() + 30);
    return {
      kitchen_id: kid,
      items: cartItems.map(item => ({
        menu_item_id: item.id,
        quantity: item.quantity,
        unit_price: item.price,
        cooking_request: item.cookingRequest || '',
      })),
      pickup_date: selectedDate.toISOString(),
      pickup_time: selectedTime,
      pickup_start: pickupStart.toISOString(),
      pickup_end: pickupEnd.toISOString(),
      slot_start_time: pickupStart.toISOString(),
      slot_end_time: pickupEnd.toISOString(),
      subtotal,
      tax_amount: subtotal * TAX_RATE,
      platform_fee: PLATFORM_FEE,
      tip_amount: tip,
      discount_amount: discount,
      total_amount: subtotal + PLATFORM_FEE + subtotal * TAX_RATE + tip - discount,
      payment_method: pmLabel,
      coupon_code: appliedCoupon?.code || null,
      cooking_instructions: cookingInstructions,
      order_type: 'pickup',
      delivery_type: 'pickup',
    };
  };

  const handlePlaceOrder = async () => {
    if (!paymentMethod) { setOrderError('Please select a payment method'); return; }
    setOrderError('');
    setPlacing(true);
    try {
      if (paymentMethod === 'online_payment') {
        const orderData = buildOrderData('stripe');
        const res = await apiService.createStripeCheckoutSession({
          ...orderData,
          success_url: `${window.location.origin}/customer/order-confirmation?success=1`,
          cancel_url: window.location.href,
        });
        if (res?.success && res.data?.checkout_url) {
          clearCart();
          window.location.href = res.data.checkout_url;
          return;
        }
      }
      // Pay on pickup
      const orderData = buildOrderData('pay_on_delivery');
      const res = await apiService.createOrder(orderData);
      if (res?.success) {
        clearCart();
        setConfirmedOrderId(res.data?.order?.id || res.data?.id || 'N/A');
        setPanelMode('confirmed');
      } else {
        setOrderError(res?.error || 'Failed to place order. Please try again.');
      }
    } catch (e) {
      setOrderError('Network error. Please try again.');
    } finally {
      setPlacing(false);
    }
  };

  // ── Menu grouping ─────────────────────────────────────────────────────────
  const categories = [...new Set(menuItems.map(i => i.category || i.category_name || 'Menu'))];

  const filteredItems = menuItems.filter(item => {
    // Determine if item is veg: use is_veg boolean if present, else compare food_type exactly
    const itemIsVeg = item.is_veg !== undefined ? !!item.is_veg : item.food_type === 'veg';
    if (filterVeg && !itemIsVeg) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (
        (item.item_name || item.name || '').toLowerCase().includes(q) ||
        (item.description || '').toLowerCase().includes(q)
      );
    }
    return true;
  });

  const itemsByCategory = categories.reduce((acc, cat) => {
    acc[cat] = filteredItems.filter(i => (i.category || i.category_name || 'Menu') === cat);
    return acc;
  }, {});

  const itemCountByCategory = categories.reduce((acc, cat) => {
    acc[cat] = menuItems.filter(i => (i.category || i.category_name || 'Menu') === cat).length;
    return acc;
  }, {});

  // ── Info helpers ──────────────────────────────────────────────────────────
  const isOpen = kitchen?.status !== 'closed' && kitchen?.is_available !== false;

  const getEstablishmentType = () => {
    if (!kitchen) return '';
    const type = (kitchen.establishment_type || kitchen.kitchen_type || '').toLowerCase();
    if (type.includes('restaurant')) return 'Restaurant';
    if (type.includes('cloud')) return 'Cloud Kitchen';
    if (type.includes('home')) return 'Home Kitchen';
    return kitchen.cuisine_type || '';
  };

  const getFullAddress = () => {
    if (!kitchen) return '';
    return [kitchen.address_street, kitchen.address_city, expandState(kitchen.address_state), kitchen.address_zip]
      .filter(Boolean).join(', ');
  };

  if (!kitchen && menuLoading) return (
    <div className="page-container"><LoadingSpinner text="Loading kitchen..." /></div>
  );

  const cartItemCount = cartItems.reduce((s, i) => s + i.quantity, 0);

  return (
    <div className="kdp-layout">
      {/* ── LEFT PANEL (75%) ── */}
      <div className="kdp-left">
        {/* Back */}
        <div className="kdp-header-top" style={{ padding: '14px 24px 0' }}>
          <button className="kdp-back-link" onClick={() => {
            if (locationSlug) navigate(`/customer/${locationSlug}`);
            else navigate(-1);
          }}>
            <span>←</span> Back
          </button>
        </div>

        {/* Compact kitchen info card */}
        <div className="kdp-compact-info">
          <div className="kdp-ci-img-wrap">
            <img
              src={resolveImageUrl(
                kitchen?.profile_image_url || kitchen?.profile_image ||
                kitchen?.logo_url || kitchen?.cover_image_url ||
                kitchen?.image_url
              ) || 'https://images.unsplash.com/photo-1563379091339-03b21ab4a4f8?w=200&h=200&fit=crop'}
              alt={kitchen?.kitchen_name || kitchen?.name || 'Kitchen'}
              className="kdp-ci-img"
              onError={(e) => { e.target.onerror = null; e.target.src = 'https://images.unsplash.com/photo-1563379091339-03b21ab4a4f8?w=200&h=200&fit=crop'; }}
            />
          </div>
          <div className="kdp-ci-center">
            {/* Line 1: Name | Open/Closed */}
            <div className="kdp-ci-info-line">
              <h1 className="kdp-ci-name">{kitchen?.kitchen_name || kitchen?.name}</h1>
              <span className={`kdp-ci-status ${isOpen ? 'open' : 'closed'}`}>
                <span className="kdp-ci-status-dot" />{isOpen ? 'Open' : 'Closed'}
              </span>
            </div>
            {/* Line 2: Address | Timing */}
            <div className="kdp-ci-info-line">
              {getFullAddress() && (
                <span className="kdp-ci-meta-item">
                  <MapPin size={12} /> {getFullAddress()}
                </span>
              )}
              {(() => {
                const formatT = (t) => {
                  if (!t) return '';
                  const [h, m] = t.split(':').map(Number);
                  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
                };
                const open = formatT(kitchen?.opening_time || kitchen?.open_time);
                const close = formatT(kitchen?.closing_time || kitchen?.close_time);
                return open && close ? (
                  <span className="kdp-ci-meta-right" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, position: 'relative' }}>
                    {open} – {close}
                    <button
                      className="hours-info-btn"
                      onMouseEnter={() => setShowHoursTooltip(true)}
                      onMouseLeave={() => setShowHoursTooltip(false)}
                      onClick={() => setShowHoursModal(true)}
                      title="View all business hours"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px', lineHeight: 1, color: '#6b7280', display: 'inline-flex', alignItems: 'center' }}
                    >
                      <Info size={13} />
                    </button>
                    {showHoursTooltip && (
                      <div className="hours-tooltip" style={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        marginTop: 8,
                        background: '#fff',
                        border: '1px solid #e5e7eb',
                        borderRadius: 8,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        padding: '12px',
                        minWidth: 260,
                        zIndex: 1000,
                        fontSize: 13,
                      }}>
                        <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 14 }}>Business Hours</div>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <tbody>
                            {(() => {
                              const DAYS = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'];
                              const DAY_LABELS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
                              const todayIdx = new Date().getDay();
                              const rows = [];
                              for (let i = 0; i < 7; i++) {
                                const dayIdx = (todayIdx + i) % 7;
                                const dayKey = DAYS[dayIdx];
                                const label = i === 0 ? `Today (${DAY_LABELS[dayIdx]})` : DAY_LABELS[dayIdx];
                                const oh = kitchen?.operating_hours;
                                let openStr = '', closeStr = '';
                                if (oh && oh[dayKey]) {
                                  const dayHours = oh[dayKey];
                                  if (dayHours.enabled === false) {
                                    openStr = 'Closed'; closeStr = '';
                                  } else {
                                    openStr = formatT(dayHours.open || dayHours.opening || dayHours.start);
                                    closeStr = formatT(dayHours.close || dayHours.closing || dayHours.end);
                                  }
                                } else if (i === 0) {
                                  openStr = formatT(kitchen?.opening_time || kitchen?.open_time);
                                  closeStr = formatT(kitchen?.closing_time || kitchen?.close_time);
                                } else {
                                  openStr = formatT(kitchen?.opening_time || kitchen?.open_time);
                                  closeStr = formatT(kitchen?.closing_time || kitchen?.close_time);
                                }
                                rows.push({ label, openStr, closeStr, isToday: i === 0 });
                              }
                              return rows.map((row, idx) => (
                                <tr key={idx} style={{ borderBottom: idx < rows.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                                  <td style={{ padding: '6px 0', fontWeight: row.isToday ? 600 : 400, color: row.isToday ? '#111827' : '#6b7280' }}>
                                    {row.label}
                                  </td>
                                  <td style={{ padding: '6px 0', textAlign: 'right', fontWeight: row.isToday ? 600 : 400, color: row.isToday ? '#111827' : '#6b7280' }}>
                                    {row.closeStr ? `${row.openStr} – ${row.closeStr}` : row.openStr}
                                  </td>
                                </tr>
                              ));
                            })()}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </span>
                ) : null;
              })()}
            </div>
            {/* Line 3: Phone | Distance */}
            <div className="kdp-ci-info-line">
              {kitchen?.contact_phone && (
                <span className="kdp-ci-meta-item">
                  <Phone size={12} /> {formatUSPhone(kitchen.contact_phone)}
                </span>
              )}
              {(() => {
                const sLat = selectedLocation?.latitude;
                const sLng = selectedLocation?.longitude;
                const kLat = kitchen?.coordinates_lat;
                const kLng = kitchen?.coordinates_lng;
                let dist = '';
                if (kitchen?.distance_miles != null) dist = `${parseFloat(kitchen.distance_miles).toFixed(1)} mi away`;
                else if (kitchen?.distance != null) dist = `${parseFloat(kitchen.distance).toFixed(1)} mi away`;
                else if (sLat && sLng && kLat && kLng) {
                  const R = 3958.8;
                  const dLat = ((kLat - sLat) * Math.PI) / 180;
                  const dLon = ((kLng - sLng) * Math.PI) / 180;
                  const a = Math.sin(dLat/2)**2 + Math.cos(sLat*Math.PI/180)*Math.cos(kLat*Math.PI/180)*Math.sin(dLon/2)**2;
                  dist = `${(R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a))).toFixed(1)} mi away`;
                }
                return dist ? <span className="kdp-ci-meta-right">{dist}</span> : null;
              })()}
            </div>
          </div>
          <button className="kdp-fav-btn" onClick={handleToggleFavorite} title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}>
            <Heart size={18} fill={isFavorite ? 'currentColor' : 'none'} color={isFavorite ? '#ef4444' : '#6b7280'} />
          </button>
        </div>

        {/* Menu */}
        <div className="menu-section">
          <h2 className="menu-title">Menu</h2>

          {/* Search + Veg filter */}
          <div className="menu-controls">
            <div className="menu-search-bar">
              <Search size={18} />
              <input
                type="text"
                placeholder="Search menu..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="menu-search-input"
              />
              {searchQuery && (
                <button
                  className="menu-search-clear-btn"
                  onClick={() => setSearchQuery('')}
                  title="Clear search"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px', color: '#9ca3af', display: 'flex', alignItems: 'center' }}
                >
                  <X size={16} />
                </button>
              )}
            </div>
            <button
              className={"filter-chip veg-filter" + (filterVeg ? ' active' : '')}
              onClick={() => setFilterVeg(!filterVeg)}
            >
              <Leaf size={16} /> Veg Only
            </button>
          </div>

          {/* Category tabs with item counts + scroll arrows */}
          {categories.length > 1 && (
            <div className="category-tabs-wrapper">
              {catScrollPos > 4 && (
                <button
                  className="cat-tabs-scroll-btn cat-tabs-scroll-left"
                  onClick={() => {
                    catTabsRef.current?.scrollBy({ left: -240, behavior: 'smooth' });
                  }}
                  aria-label="Scroll left"
                >
                  <ChevronLeft size={16} />
                </button>
              )}
              <div
                className="category-tabs"
                ref={catTabsRef}
                onScroll={() => {
                  const el = catTabsRef.current;
                  if (!el) return;
                  setCatScrollPos(el.scrollLeft);
                  setCatMaxScroll(el.scrollWidth - el.clientWidth);
                }}
              >
                <button
                  className={"cat-tab" + (!activeCategory ? ' active' : '')}
                  onClick={() => setActiveCategory(null)}
                >
                  <span className="cat-tab-icon">🔥</span> All <span className="cat-count">{menuItems.length}</span>
                </button>
                {categories.map(cat => {
                  const icon = cat.toLowerCase().includes('pulav') ? '🍚' : 
                               cat.toLowerCase().includes('biryani') ? '🍛' :
                               cat.toLowerCase().includes('fried') ? '🍗' :
                               cat.toLowerCase().includes('noodle') ? '🍜' :
                               cat.toLowerCase().includes('rice') ? '🍚' :
                               cat.toLowerCase().includes('curry') || cat.toLowerCase().includes('curries') ? '🍛' :
                               cat.toLowerCase().includes('soup') ? '🍲' : '🍽️';
                  return (
                    <button
                      key={cat}
                      className={"cat-tab" + (activeCategory === cat ? ' active' : '')}
                      onClick={() => setActiveCategory(cat)}
                    >
                      <span className="cat-tab-icon">{icon}</span> {cat} <span className="cat-count">{itemCountByCategory[cat]}</span>
                    </button>
                  );
                })}
              </div>
              {catScrollPos < catMaxScroll - 4 && (
                <button
                  className="cat-tabs-scroll-btn cat-tabs-scroll-right"
                  onClick={() => {
                    catTabsRef.current?.scrollBy({ left: 240, behavior: 'smooth' });
                  }}
                  aria-label="Scroll right"
                >
                  <ChevronRight size={16} />
                </button>
              )}
            </div>
          )}

          {menuLoading ? (
            <LoadingSpinner text="Loading menu..." />
          ) : (
            <div className="menu-categories">
              {(activeCategory ? [activeCategory] : categories).map(cat => {
                const items = itemsByCategory[cat] || [];
                if (items.length === 0) return null;
                return (
                  <div key={cat} className="menu-category">
                    <h3 className="menu-category-title">
                      {cat} <span className="menu-cat-count">({itemCountByCategory[cat]})</span>
                    </h3>
                    {/* Horizontal cards list */}
                    <div className="menu-items-list">
                      {items.map(item => {
                        const itemId = item.item_id || item.id;
                        const qty = getItemQuantityInCart(itemId);
                        const isVeg = item.is_veg !== undefined ? !!item.is_veg : item.food_type === 'veg';
                        return (
                          <div key={itemId} className="menu-item-card">
                            <div className="menu-item-img-wrap">
                              <img
                                src={resolveImageUrl(item.image_url) || `https://images.unsplash.com/photo-1563379091339-03b21ab4a4f8?w=200&h=200&fit=crop&sig=${itemId}`}
                                alt={item.item_name || item.name}
                                className="menu-item-img"
                                loading="lazy"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = `https://images.unsplash.com/photo-1563379091339-03b21ab4a4f8?w=200&h=200&fit=crop&sig=${itemId}`;
                                }}
                              />
                              {item.is_bestseller && (
                                <span className="menu-bestseller-badge">⭐ Bestseller</span>
                              )}
                            </div>
                            <div className="menu-item-info">
                              <div className="menu-item-header">
                                <h4 className="menu-item-name">
                                  {isVeg !== undefined && (
                                    <span className={"veg-indicator " + (isVeg ? 'veg' : 'non-veg')} />
                                  )}
                                  {item.item_name || item.name}
                                </h4>
                              </div>
                              {item.description && (
                                <p className="menu-item-desc">{item.description}</p>
                              )}
                              <div className="menu-item-footer">
                                <span className="menu-item-price">${parseFloat(item.price || 0).toFixed(2)}</span>
                                {qty === 0 ? (
                                  <button className="menu-add-btn" onClick={() => handleAddToCart(item)}>+ Add</button>
                                ) : (
                                  <div className="menu-qty-controls">
                                    <button className="qty-btn" onClick={() => {
                                      if (qty === 1) removeFromCart(itemId);
                                      else updateQuantity(itemId, qty - 1);
                                    }}>−</button>
                                    <span className="qty-value">{qty}</span>
                                    <button className="qty-btn" onClick={() => updateQuantity(itemId, qty + 1)}>+</button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── RIGHT PANEL (35%) — Cart / Review / Confirmed ── */}
      <div className="kdp-right">
        <div className="kdp-cart-panel">

          {/* ══ CONFIRMED PANEL ══════════════════════════════════════════════ */}
          {panelMode === 'confirmed' && (
            <div className="kdp-confirmed">
              <CheckCircle size={56} color="#22c55e" />
              <h3 className="kdp-confirmed-title">Order Placed! 🎉</h3>
              <p className="kdp-confirmed-sub">Your order has been confirmed.</p>
              {confirmedOrderId && (
                <p className="kdp-confirmed-id">Order ID: <strong>#{confirmedOrderId}</strong></p>
              )}
              <button
                className="btn-primary btn-full"
                style={{ marginTop: 20 }}
                onClick={() => navigate('/customer/orders')}
              >
                View My Orders
              </button>
              <button
                className="btn-outline btn-full"
                style={{ marginTop: 10 }}
                onClick={() => {
                  setPanelMode('cart');
                  setConfirmedOrderId(null);
                }}
              >
                Continue Shopping
              </button>
            </div>
          )}

          {/* ══ CHECKOUT PANEL ═══════════════════════════════════════════════ */}
          {panelMode === 'checkout' && (
            <>
              <div className="kdp-cart-header">
                <button className="kdp-back-btn" onClick={() => setPanelMode('cart')}>
                  <ArrowLeft size={16} /> Back
                </button>
                <h3 className="kdp-cart-title">Review & Pay</h3>
              </div>

              {/* Order items (read-only) */}
              <div className="kdp-cart-items">
                {cartItems.map(item => (
                  <div key={item.id} className="kdp-cart-item">
                    <div className="kdp-cart-item-top">
                      <span className="kdp-cart-item-name">{item.name}</span>
                      <span className="kdp-cart-item-price">${(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                      {item.quantity} × ${item.price.toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>

              {/* Pickup details */}
              <div className="kdp-cart-section">
                <h4 className="kdp-cart-section-title"><Calendar size={14} /> Pickup Details</h4>
                <p className="kdp-checkout-detail">{shortDateLabel(selectedDate)} at {formatTimeDisplay(selectedTime)}</p>
                <p className="kdp-checkout-detail" style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
                  📍 {cartRestaurant?.name || kitchen?.kitchen_name || kitchen?.name}
                </p>
              </div>

              {/* Tip */}
              <div className="kdp-cart-section">
                <h4 className="kdp-cart-section-title">Tip</h4>
                <div className="tip-row">
                  {[{ label: 'None', pct: 0 }, { label: '10%', pct: 10 }, { label: '15%', pct: 15 }, { label: '20%', pct: 20 }, { label: 'Custom', pct: -1 }].map(opt => (
                    <button
                      key={opt.label}
                      className={"tip-chip" + (tipPct === opt.pct ? ' selected' : '')}
                      onClick={() => { setTipPct(opt.pct); if (opt.pct !== -1) setCustomTipAmount(''); }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                {tipPct === -1 && (
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="form-input"
                    placeholder="Enter custom tip ($)"
                    value={customTipAmount}
                    onChange={(e) => setCustomTipAmount(e.target.value)}
                    style={{ marginTop: 8 }}
                  />
                )}
              </div>

              {/* Payment method */}
              <div className="kdp-cart-section">
                <h4 className="kdp-cart-section-title">Payment Method</h4>
                {[
                  { id: 'online_payment', label: 'Online Payment', sub: 'Stripe', icon: '💳' },
                  { id: 'pay_on_delivery', label: 'Pay on Pickup', sub: 'Cash or card', icon: '💵' },
                ].map(pm => (
                  <div
                    key={pm.id}
                    className={"payment-option" + (paymentMethod === pm.id ? ' selected' : '')}
                    onClick={() => setPaymentMethod(pm.id)}
                  >
                    <span className="payment-icon">{pm.icon}</span>
                    <div className="payment-info">
                      <span className="payment-label">{pm.label}</span>
                      <span className="payment-sub">{pm.sub}</span>
                    </div>
                    <div className={"radio-circle" + (paymentMethod === pm.id ? ' active' : '')} />
                  </div>
                ))}
              </div>

              {/* Bill summary */}
              <div className="kdp-cart-section kdp-bill-summary">
                <h4 className="kdp-cart-section-title">Bill Summary</h4>
                <div className="price-row"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
                <div className="price-row"><span>Platform Fee</span><span>${PLATFORM_FEE.toFixed(2)}</span></div>
                <div className="price-row"><span>Tax (5%)</span><span>${(subtotal * TAX_RATE).toFixed(2)}</span></div>
                {tip > 0 && <div className="price-row"><span>Tip</span><span>${tip.toFixed(2)}</span></div>}
                {discount > 0 && <div className="price-row discount"><span>Discount</span><span>-${discount.toFixed(2)}</span></div>}
                <hr className="price-divider" />
                <div className="price-row total">
                  <span>Total</span>
                  <span>${(subtotal + PLATFORM_FEE + subtotal * TAX_RATE + tip - discount).toFixed(2)}</span>
                </div>
              </div>

              {orderError && <p className="kdp-order-error">{orderError}</p>}

              <button
                className="btn-primary btn-full checkout-btn"
                onClick={handlePlaceOrder}
                disabled={placing || !paymentMethod}
              >
                {placing ? 'Placing Order…' : `Place Order · $${(subtotal + PLATFORM_FEE + subtotal * TAX_RATE + tip - discount).toFixed(2)}`}
              </button>
            </>
          )}

          {/* ══ CART PANEL ═══════════════════════════════════════════════════ */}
          {panelMode === 'cart' && (<>
          <div className="kdp-cart-header">
            <ShoppingCart size={20} />
            <h3 className="kdp-cart-title">Your Cart</h3>
            {cartItemCount > 0 && (
              <span className="kdp-cart-count">{cartItemCount} item{cartItemCount !== 1 ? 's' : ''}</span>
            )}
          </div>

          {/* Cart kitchen info — always from the cart's restaurant, never the viewed kitchen */}
          {cartItems.length > 0 && cartRestaurant && (
            <div className="kdp-cart-kitchen-info">
              <div className="kdp-cart-kitchen-name">{cartRestaurant.name}</div>
              {cartRestaurant.address && (
                <div className="kdp-cart-kitchen-addr">
                  <MapPin size={11} /> {cartRestaurant.address}
                </div>
              )}
            </div>
          )}

          {cartItems.length === 0 ? (
            <div className="kdp-cart-empty">
              <span className="kdp-cart-empty-icon">🛒</span>
              <p>Your cart is empty</p>
              <small>Add items from the menu to get started</small>
            </div>
          ) : (
            <>
              {/* Cart Items */}
              <div className="kdp-cart-items">
                {cartItems.map(item => (
                  <div key={item.id} className="kdp-cart-item">
                    {/* Veg/Non-veg indicator positioned on image */}
                    <div className="kdp-cart-item-img-wrapper">
                      <img
                        src={item.image_url ? resolveImageUrl(item.image_url) : `https://images.unsplash.com/photo-1563379091339-03b21ab4a4f8?w=80&h=80&fit=crop`}
                        alt={item.name}
                        className="kdp-cart-item-img"
                        loading="lazy"
                        onError={(e) => {
                          e.target.src = `https://images.unsplash.com/photo-1563379091339-03b21ab4a4f8?w=80&h=80&fit=crop`;
                        }}
                      />
                      <div className={`kdp-veg-badge ${(item.is_veg === false || item.food_type === 'non_veg') ? 'non-veg' : 'veg'}`}>
                        <div className="kdp-veg-dot" />
                      </div>
                    </div>
                    <div className="kdp-cart-item-details">
                      <div className="kdp-cart-item-top">
                        <span className="kdp-cart-item-name">{item.name}</span>
                        <div className="qty-controls qty-controls-sm">
                          <button className="qty-btn" onClick={() => {
                            if (item.quantity === 1) removeFromCart(item.id);
                            else updateQuantity(item.id, item.quantity - 1);
                          }}>−</button>
                          <span className="qty-value">{item.quantity}</span>
                          <button className="qty-btn" onClick={() => updateQuantity(item.id, item.quantity + 1)}>+</button>
                        </div>
                      </div>
                      <div className="kdp-cart-item-bottom">
                        <span className="kdp-cart-item-price">${(item.price * item.quantity).toFixed(2)}</span>
                        <button className="kdp-cart-remove" onClick={() => removeFromCart(item.id)}>
                          <Trash2 size={13} />
                        </button>
                      </div>
                      <input
                        type="text"
                        className="cooking-request-input"
                        placeholder="Add cooking request..."
                        value={item.cookingRequest || ''}
                        onChange={(e) => updateCookingRequest(item.id, e.target.value)}
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Cooking Instructions */}
              <div className="kdp-cart-section">
                <h4 className="kdp-cart-section-title">Special Instructions</h4>
                <textarea
                  className="cooking-instructions"
                  placeholder="Any special instructions for the entire order..."
                  value={cookingInstructions}
                  onChange={(e) => setCookingInstructions(e.target.value)}
                  rows={2}
                />
              </div>

              {/* Pickup Time */}
              <div className="kdp-cart-section">
                <h4 className="kdp-cart-section-title"><Calendar size={14} /> Pickup Time</h4>
                <div className="date-scroll">
                  {dates.map((date, idx) => (
                    <button
                      key={idx}
                      className={"date-chip" + (formatDateISO(date) === formatDateISO(selectedDate) ? ' selected' : '')}
                      onClick={() => setSelectedDate(date)}
                    >
                      {shortDateLabel(date)}
                    </button>
                  ))}
                </div>
                {slotsLoading ? (
                  <p className="slots-loading">Loading slots...</p>
                ) : slots.length === 0 ? (
                  <p className="no-slots">No slots available</p>
                ) : (
                  <div className="slots-grid">
                    {slots.map((slot, idx) => (
                      <button
                        key={idx}
                        className={"slot-chip" + (selectedTime === slot.time ? ' selected' : '')}
                        onClick={() => setSelectedTime(slot.time)}
                      >
                        {formatTimeDisplay(slot.time)}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Coupon */}
              <div className="kdp-cart-section">
                <div className="coupon-header">
                  <h4 className="kdp-cart-section-title"><Tag size={14} /> Coupon</h4>
                  {!appliedCoupon && (
                    <button className="apply-coupon-toggle" onClick={() => setShowCouponInput(!showCouponInput)}>
                      {showCouponInput ? 'Cancel' : 'Apply'}
                    </button>
                  )}
                </div>
                {appliedCoupon ? (
                  <div className="coupon-applied">
                    <span>✅ {appliedCoupon.code} — Saved ${appliedCoupon.amount}</span>
                    <button className="remove-coupon" onClick={() => { setDiscount(0); setAppliedCoupon(null); }}>✕</button>
                  </div>
                ) : showCouponInput ? (
                  <div className="coupon-input-row">
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Coupon code"
                      value={couponInput}
                      onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                      onKeyDown={(e) => e.key === 'Enter' && handleApplyCoupon()}
                    />
                    <button className="btn-primary" onClick={handleApplyCoupon} disabled={couponLoading}>
                      {couponLoading ? '...' : 'Apply'}
                    </button>
                  </div>
                ) : null}
                {couponError && <p className="coupon-error">{couponError}</p>}
              </div>

              {/* Bill Summary */}
              <div className="kdp-cart-section kdp-bill-summary">
                <h4 className="kdp-cart-section-title">Review Order</h4>
                <div className="price-row"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
                <div className="price-row"><span>Platform Fee</span><span>${PLATFORM_FEE.toFixed(2)}</span></div>
                <div className="price-row"><span>Tax (5%)</span><span>${tax.toFixed(2)}</span></div>
                {discount > 0 && (
                  <div className="price-row discount"><span>Discount</span><span>-${discount.toFixed(2)}</span></div>
                )}
                <hr className="price-divider" />
                <div className="price-row total"><span>Total</span><span>${total.toFixed(2)}</span></div>
              </div>

              {/* Checkout */}
              <button
                className="btn-primary btn-full checkout-btn"
                onClick={handleCheckout}
                disabled={!selectedTime}
              >
                {selectedTime ? 'Proceed to Checkout · $' + total.toFixed(2) : 'Select a pickup time'}
              </button>
              {selectedTime && (
                <p className="kdp-pickup-label">
                  📦 {shortDateLabel(selectedDate)} at {formatTimeDisplay(selectedTime)}
                </p>
              )}
            </>
          )}
          </>)}
        </div>
      </div>

      {/* Cart feedback toast */}
      {cartFeedback && <div className="cart-toast">{cartFeedback}</div>}

      {/* Replace cart modal */}
      <ConfirmModal
        isOpen={showReplaceModal}
        onClose={() => { setShowReplaceModal(false); setPendingItem(null); }}
        onConfirm={handleReplaceCart}
        title="Replace Cart?"
        message={`Your cart has items from ${cartRestaurant?.name || 'another kitchen'}. Replace with items from ${kitchen?.kitchen_name || kitchen?.name}?`}
        confirmText="Replace Cart"
        dangerMode
      />

      {/* Business Hours Modal */}
      {showHoursModal && (
        <div className="modal-overlay" onClick={() => setShowHoursModal(false)}>
          <div className="modal-container modal-medium" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 380 }}>
            <div className="modal-header">
              <h3 className="modal-title">Business Hours</h3>
              <button className="modal-close" onClick={() => setShowHoursModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              {(() => {
                const DAYS = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'];
                const DAY_LABELS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
                const todayIdx = new Date().getDay();
                const formatT = (t) => {
                  if (!t) return 'Closed';
                  const [h, m] = t.split(':').map(Number);
                  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
                };
                // Build list starting from today
                const rows = [];
                for (let i = 0; i < 7; i++) {
                  const dayIdx = (todayIdx + i) % 7;
                  const dayKey = DAYS[dayIdx];
                  const label = i === 0 ? `Today (${DAY_LABELS[dayIdx]})` : DAY_LABELS[dayIdx];
                  const oh = kitchen?.operating_hours;
                  let openStr = '', closeStr = '';
                  if (oh && oh[dayKey]) {
                    const dayHours = oh[dayKey];
                    if (dayHours.enabled === false) {
                      openStr = 'Closed'; closeStr = '';
                    } else {
                      openStr = formatT(dayHours.open || dayHours.opening || dayHours.start);
                      closeStr = formatT(dayHours.close || dayHours.closing || dayHours.end);
                    }
                  } else if (i === 0) {
                    openStr = formatT(kitchen?.opening_time || kitchen?.open_time);
                    closeStr = formatT(kitchen?.closing_time || kitchen?.close_time);
                  } else {
                    openStr = formatT(kitchen?.opening_time || kitchen?.open_time);
                    closeStr = formatT(kitchen?.closing_time || kitchen?.close_time);
                  }
                  rows.push({ label, openStr, closeStr, isToday: i === 0 });
                }
                return (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                    <tbody>
                      {rows.map((r, idx) => (
                        <tr key={idx} style={{ background: r.isToday ? '#fef9ec' : 'transparent', fontWeight: r.isToday ? 600 : 400 }}>
                          <td style={{ padding: '8px 4px', color: '#374151' }}>{r.label}</td>
                          <td style={{ padding: '8px 4px', textAlign: 'right', color: r.openStr === 'Closed' ? '#ef4444' : '#16a34a' }}>
                            {r.openStr === 'Closed' ? 'Closed' : `${r.openStr}${r.closeStr ? ` – ${r.closeStr}` : ''}`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KitchenDetailPage;
